import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { AccountSave } from '../../game/account';
import type { CharacterSave } from '../../game/character';
import type { Game } from '../../game/Game';
import { countOf } from '../../game/inventory';
import { ITEMS } from '../../game/items';
import { Window } from '../../ui/app/Window';
import { bump, ui } from '../../ui/app/store';
import { genericIcon } from '../../ui/icons';
import {
  CATEGORY_NAMES,
  CREW,
  CROPS,
  type CropDef,
  GROWTH_STAGES,
  HAZARDS,
  MAX_BATCHES,
  MAX_CRAFT_QUEUE,
  MISSIONS,
  MISSION_BY_ID,
  type MissionDef,
  RECIPES,
  RECIPE_BY_ID,
  RESEARCH,
  RESEARCH_BY_ID,
  type RecipeCategory,
  type RecipeDef,
  type ResearchDef,
} from './data';
import {
  type Result,
  autoCrew,
  busyCrew,
  craftSeconds,
  craftStart,
  formatDuration,
  gardenView,
  harvestGarden,
  havenBonus,
  havenLevelProgress,
  maxBatches,
  missionOdds,
  missionUnlocked,
  nextHavenTimer,
  plantCrop,
  queueCraft,
  recipeUnlocked,
  resolveInputs,
  researchCost,
  researchLevel,
  researchStatus,
  roman,
  startDispatch,
  startResearch,
} from './logic';
import { HAVEN_TABS, type HavenTab, havenGame } from './runtime';
import { Bar, ItemIcon, ItemLine, Silver, cssHue, itemColor, itemName } from './widgets';

/** Haven Isle management window: Research / Workshop / Dispatch / Garden. */

const TAB_LABEL: Record<HavenTab, string> = { research: 'Research', workshop: 'Workshop', dispatch: 'Dispatch', garden: 'Garden' };
const TIER_NAME = ['', 'Foundations', 'Craftsmanship', 'Mastery'];
const MIN = 60_000;

function first<T>(list: readonly T[]): T {
  const v = list[0];
  if (v === undefined) throw new Error('Empty Haven content table');
  return v;
}

interface Ctx {
  game: Game;
  a: AccountSave;
  c: CharacterSave;
  now: number;
  act: (r: Result, ok?: string) => void;
}

function Lock() {
  return (
    <svg viewBox="0 0 24 24" class="hv-lock" aria-label="Locked">
      <rect x="5" y="11" width="14" height="10" rx="1.5" />
      <path d="M8 11 V8 a4 4 0 0 1 8 0 V11" fill="none" />
    </svg>
  );
}

function Clock() {
  return (
    <svg viewBox="0 0 24 24" class="hv-clock">
      <circle cx="12" cy="12" r="8.5" fill="none" />
      <path d="M12 7.5 V12 L15 14" fill="none" />
    </svg>
  );
}

// ================================================================ research

function ResearchCard({ def, x, sel, onSel }: { def: ResearchDef; x: Ctx; sel: boolean; onSel: () => void }) {
  const h = x.a.haven;
  const lvl = researchLevel(h, def.id);
  const st = researchStatus(h, def.id).status;
  const active = h.researching?.id === def.id ? h.researching : null;
  const total = researchCost(def, lvl + 1).minutes * MIN;
  return (
    <button class={`hv-rcard ${st}${sel ? ' sel' : ''}`} onClick={onSel}>
      <img class="hv-ic" src={genericIcon(`research:${def.id}`, def.icon.hue, def.icon.glyph)} alt="" />
      <span class="hv-rc-main">
        <span class="hv-rc-name">{def.name}</span>
        <span class="hv-pips">
          {def.levels.map((_, i) => (
            <i key={i} class={i < lvl ? 'on' : active && i === lvl ? 'wip' : ''} />
          ))}
        </span>
      </span>
      {st === 'locked' && <Lock />}
      {active && <Bar tone="blue" value={1 - (active.doneAt - x.now) / total} />}
    </button>
  );
}

function ResearchDetail({ def, x }: { def: ResearchDef; x: Ctx }) {
  const h = x.a.haven;
  const lvl = researchLevel(h, def.id);
  const st = researchStatus(h, def.id);
  const maxed = lvl >= def.levels.length;
  const cost = researchCost(def, lvl + 1);
  const affordable = x.a.wallet.silver >= (cost.silver ?? 0) && Object.entries(cost.items ?? {}).every(([id, n]) => countOf(x.c, id) >= n);
  const active = h.researching;
  const busyWith = active && active.id !== def.id ? RESEARCH_BY_ID[active.id] : null;
  return (
    <div class="hv-detail">
      <div class="hv-d-head">
        <img class="hv-ic big" src={genericIcon(`research:${def.id}`, def.icon.hue, def.icon.glyph)} alt="" />
        <div>
          <div class="hv-d-name">{def.name}</div>
          <div class="hv-d-sub">
            Tier {roman(def.tier)} · Level {lvl} / {def.levels.length}
          </div>
        </div>
      </div>
      <p class="hv-d-desc">{def.description}</p>
      <div class="section-h">Effects</div>
      <ul class="hv-levels">
        {def.levels.map((_, i) => (
          <li key={i} class={i < lvl ? 'done' : i === lvl ? 'next' : ''}>
            <span class="hv-lv-n">{roman(i + 1)}</span>
            {def.effect(i + 1)}
          </li>
        ))}
      </ul>
      {!maxed && (
        <>
          <div class="section-h">Cost — level {roman(lvl + 1)}</div>
          <div class="hv-cost">
            {Object.entries(cost.items ?? {}).map(([id, n]) => (
              <ItemLine key={id} id={id} count={n} have={countOf(x.c, id)} />
            ))}
            <div class="hv-cost-row">
              <Silver n={cost.silver ?? 0} />
              <span class="hv-time">
                <Clock /> {cost.minutes} min
              </span>
            </div>
          </div>
        </>
      )}
      <div class="hv-d-foot">
        {st.status === 'active' && active ? (
          <div class="hv-progress">
            <Bar tone="blue" value={1 - (active.doneAt - x.now) / (cost.minutes * MIN)} label={`Completes in ${formatDuration(active.doneAt - x.now)}`} />
          </div>
        ) : st.status === 'maxed' ? (
          <div class="hv-note ok">Fully researched</div>
        ) : st.status === 'available' ? (
          <button class="btn btn--gold hv-go" disabled={!affordable} onClick={() => x.act(startResearch(x.a, x.c, def.id, Date.now()), `Research started: ${def.name}`)}>
            {affordable ? `Begin research (${cost.minutes} min)` : 'Missing materials'}
          </button>
        ) : (
          <div class="hv-note">{busyWith ? `The lab is busy with ${busyWith.name}` : st.reason}</div>
        )}
      </div>
    </div>
  );
}

function ResearchTab({ x }: { x: Ctx }) {
  const h = x.a.haven;
  const [sel, setSel] = useState<string>(h.researching?.id ?? first(RESEARCH).id);
  const def = RESEARCH_BY_ID[sel] ?? first(RESEARCH);
  const lvl = havenLevelProgress(h.xp).level;
  return (
    <div class="hv-split">
      <div class="hv-tree">
        {([1, 2, 3] as const).map((tier) => {
          const gate = Math.min(...RESEARCH.filter((r) => r.tier === tier).map((r) => r.havenLevel));
          return (
            <section key={tier} class={`hv-tier${lvl < gate ? ' locked' : ''}`}>
              <div class="hv-tier-h">
                <span class="ornament" /> Tier {roman(tier)} <small>{TIER_NAME[tier]}</small>
                {lvl < gate && <span class="hv-tier-gate">Haven Lv {gate}</span>}
              </div>
              <div class="hv-tier-row">
                {RESEARCH.filter((r) => r.tier === tier).map((r) => (
                  <ResearchCard key={r.id} def={r} x={x} sel={r.id === def.id} onSel={() => setSel(r.id)} />
                ))}
              </div>
            </section>
          );
        })}
        <ActiveEffects x={x} />
      </div>
      <ResearchDetail def={def} x={x} />
    </div>
  );
}

/** Every research effect currently in force. */
function ActiveEffects({ x }: { x: Ctx }) {
  const h = x.a.haven;
  const done = RESEARCH.filter((r) => researchLevel(h, r.id) > 0);
  return (
    <section class="hv-effects">
      <div class="hv-tier-h">
        <span class="ornament" /> Active effects
      </div>
      {done.length === 0 ? (
        <div class="hv-empty">Complete research to unlock permanent bonuses.</div>
      ) : (
        <ul>
          {done.map((r) => (
            <li key={r.id}>
              <img src={genericIcon(`research:${r.id}`, r.icon.hue, r.icon.glyph)} alt="" />
              {r.effect(researchLevel(h, r.id))}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ================================================================ workshop

function RecipeRow({ r, x, sel, onSel }: { r: RecipeDef; x: Ctx; sel: boolean; onSel: () => void }) {
  const unlocked = recipeUnlocked(x.a.haven, r);
  const can = unlocked && maxBatches(x.a, x.c, r) > 0;
  return (
    <button class={`hv-row${sel ? ' sel' : ''}${unlocked ? '' : ' locked'}`} onClick={onSel}>
      <ItemIcon id={r.out.id} size={30} dim={!unlocked} />
      <span class="hv-row-name" style={{ color: unlocked ? itemColor(r.out.id) : undefined }}>
        {itemName(r.out.id)}
      </span>
      <span class="hv-row-meta">{unlocked ? `×${r.out.count}` : `Lv ${r.havenLevel}`}</span>
      <span class={`hv-dot${can ? ' on' : ''}`} title={can ? 'You have the materials' : ''} />
    </button>
  );
}

function CraftQueue({ x }: { x: Ctx }) {
  const q = x.a.haven.crafting;
  return (
    <div class="hv-queue">
      <div class="section-h">
        Workshop queue <small>{q.length} / {MAX_CRAFT_QUEUE}</small>
      </div>
      {q.length === 0 && <div class="hv-empty">The benches are idle.</div>}
      {q.map((e, i) => {
        const r = RECIPE_BY_ID[e.recipe];
        if (!r) return null;
        const start = craftStart(x.a, i);
        const running = x.now >= start;
        const p = running ? (x.now - start) / Math.max(1, e.doneAt - start) : 0;
        return (
          <div key={`${e.recipe}:${e.doneAt}`} class={`hv-qrow${running ? ' run' : ''}`}>
            <ItemIcon id={r.out.id} size={28} count={r.out.count * e.count} />
            <div class="hv-q-main">
              <span>
                {itemName(r.out.id)} <small class="muted">({e.count} batch{e.count > 1 ? 'es' : ''})</small>
              </span>
              <Bar tone="gold" value={p} />
            </div>
            <span class="hv-q-t">{running ? formatDuration(e.doneAt - x.now) : 'Queued'}</span>
          </div>
        );
      })}
    </div>
  );
}

function WorkshopTab({ x }: { x: Ctx }) {
  const [sel, setSel] = useState('potion_small');
  const [n, setN] = useState(1);
  const r = RECIPE_BY_ID[sel] ?? first(RECIPES);
  const h = x.a.haven;
  const unlocked = recipeUnlocked(h, r);
  const max = unlocked ? maxBatches(x.a, x.c, r) : 0;
  const qty = Math.max(1, Math.min(MAX_BATCHES, n));
  const plan = resolveInputs(x.c, r, qty);
  const queueFull = h.crafting.length >= MAX_CRAFT_QUEUE;
  const can = unlocked && !queueFull && qty <= max;
  const cats = [...new Set(RECIPES.map((z) => z.category))] as RecipeCategory[];
  const def = ITEMS[r.out.id];
  const secs = craftSeconds(x.a, r, qty);
  return (
    <div class="hv-split">
      <div class="hv-list">
        {cats.map((cat) => (
          <div key={cat}>
            <div class="hv-list-h">{CATEGORY_NAMES[cat]}</div>
            {RECIPES.filter((z) => z.category === cat).map((z) => (
              <RecipeRow
                key={z.id}
                r={z}
                x={x}
                sel={z.id === r.id}
                onSel={() => {
                  setSel(z.id);
                  setN(1);
                }}
              />
            ))}
          </div>
        ))}
      </div>
      <div class="hv-detail">
        <div class="hv-d-head">
          <ItemIcon id={r.out.id} size={48} count={r.out.count} />
          <div>
            <div class="hv-d-name" style={{ color: itemColor(r.out.id) }}>
              {itemName(r.out.id)}
            </div>
            <div class="hv-d-sub">{def?.description}</div>
          </div>
        </div>
        <div class="section-h">
          Materials <small>× {qty}</small>
        </div>
        <div class="hv-cost">
          {Object.entries(r.items).map(([id, k]) => (
            <ItemLine key={id} id={id} count={k * qty} have={countOf(x.c, id)} />
          ))}
          {(r.pools ?? []).map((p) => {
            const have = p.ids.reduce((s, id) => s + countOf(x.c, id), 0);
            const need = p.count * qty;
            return (
              <div key={p.label} class="hv-pool">
                <div class="fx-line">
                  <span class="hv-pool-ic">
                    {p.ids.slice(0, 4).map((id) => (
                      <ItemIcon key={id} id={id} size={12} />
                    ))}
                  </span>
                  <span class="fx-line-name">{p.label}</span>
                  <span class={`fx-line-n ${have >= need ? 'ok' : 'short'}`}>
                    {Math.min(have, 9999)}/{need}
                  </span>
                </div>
                {plan && (
                  <div class="hv-pool-use">
                    Uses{' '}
                    {p.ids
                      .filter((id) => (plan[id] ?? 0) - (r.items[id] ?? 0) * qty > 0)
                      .map((id) => `${itemName(id)} ×${(plan[id] ?? 0) - (r.items[id] ?? 0) * qty}`)
                      .join(', ')}
                  </div>
                )}
              </div>
            );
          })}
          <div class="hv-cost-row">
            <Silver n={r.silver * qty} />
            <span class="hv-time">
              <Clock /> {formatDuration(secs * 1000)}
            </span>
          </div>
        </div>
        <div class="hv-d-foot hv-craft-foot">
          <div class="hv-stepper">
            <button class="btn" disabled={qty <= 1} onClick={() => setN(qty - 1)}>
              −
            </button>
            <b>{qty}</b>
            <button class="btn" disabled={qty >= MAX_BATCHES} onClick={() => setN(qty + 1)}>
              +
            </button>
            <button class="btn" disabled={max < 1} onClick={() => setN(Math.max(1, max))}>
              Max
            </button>
          </div>
          <button
            class="btn btn--gold hv-go"
            disabled={!can}
            onClick={() => {
              x.act(queueCraft(x.a, x.c, r.id, qty, Date.now()), `Queued: ${itemName(r.out.id)} ×${r.out.count * qty}`);
              setN(1);
            }}
          >
            {!unlocked ? `Requires Haven Lv ${r.havenLevel}` : queueFull ? 'Queue full' : qty > max ? 'Missing materials' : 'Craft'}
          </button>
        </div>
        <CraftQueue x={x} />
      </div>
    </div>
  );
}

// ================================================================ dispatch

function HazardChip({ hz, ok }: { hz: keyof typeof HAZARDS; ok?: boolean }) {
  const H = HAZARDS[hz];
  return (
    <span class={`hv-hz${ok === undefined ? '' : ok ? ' ok' : ' no'}`} style={{ '--hz': H.color }}>
      {H.name}
      {ok !== undefined && <b>{ok ? '✔' : '✕'}</b>}
    </span>
  );
}

function MissionRow({ m, x, sel, onSel }: { m: MissionDef; x: Ctx; sel: boolean; onSel: () => void }) {
  const h = x.a.haven;
  const run = h.dispatches.find((d) => d.mission === m.id);
  const unlocked = missionUnlocked(h, m);
  return (
    <button class={`hv-mrow${sel ? ' sel' : ''}${unlocked ? '' : ' locked'}${run ? ' run' : ''}`} onClick={onSel}>
      <span class="hv-m-name">{m.name}</span>
      <span class="hv-m-meta">
        {m.hazards.map((hz) => (
          <HazardChip key={hz} hz={hz} />
        ))}
      </span>
      <span class="hv-m-time">{run ? formatDuration(run.doneAt - x.now) : unlocked ? `${m.minutes} min` : `Lv ${m.havenLevel}`}</span>
      {run && <Bar tone="teal" value={1 - (run.doneAt - x.now) / (m.minutes * MIN)} />}
    </button>
  );
}

function DispatchTab({ x }: { x: Ctx }) {
  const h = x.a.haven;
  const [sel, setSel] = useState(first(MISSIONS).id);
  const m = MISSION_BY_ID[sel] ?? first(MISSIONS);
  const [crew, setCrew] = useState<string[]>(() => autoCrew(h, m));
  const busy = busyCrew(h);
  const run = h.dispatches.find((d) => d.mission === m.id);
  const unlocked = missionUnlocked(h, m);
  const picked = crew.filter((n) => h.crew.includes(n) && !busy.has(n));
  const odds = missionOdds(x.a, m, picked);
  const shown = run ? missionOdds(x.a, m, run.crew) : odds;
  const R = m.rewards;
  const choose = (id: string) => {
    const next = MISSION_BY_ID[id];
    setSel(id);
    if (next) setCrew(autoCrew(h, next));
  };
  const toggle = (n: string) => setCrew(picked.includes(n) ? picked.filter((z) => z !== n) : [...picked, n]);
  return (
    <div class="hv-split">
      <div class="hv-list">
        <div class="hv-list-h">Missions</div>
        {MISSIONS.map((z) => (
          <MissionRow key={z.id} m={z} x={x} sel={z.id === m.id} onSel={() => choose(z.id)} />
        ))}
        <div class="hv-list-h">Crew</div>
        <div class="hv-crew-list">
          {h.crew.map((n) => {
            const d = h.dispatches.find((z) => z.crew.includes(n));
            return (
              <div key={n} class={`hv-crew-line${d ? ' away' : ''}`}>
                <b>{n}</b> <span class="muted">{CREW[n]?.trait ?? 'Deckhand'}</span>
                <span class="hv-crew-st">{d ? `Away · ${formatDuration(d.doneAt - x.now)}` : 'At the harbour'}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div class="hv-detail">
        <div class="hv-d-head">
          <div class="hv-ship">
            <svg viewBox="0 0 48 48">
              <path d="M8 30 L40 30 L35 38 L13 38 Z" />
              <path d="M24 8 L24 30 M24 10 L36 26 L24 26 Z M22 12 L13 26 L22 26 Z" />
            </svg>
          </div>
          <div>
            <div class="hv-d-name">{m.name}</div>
            <div class="hv-d-sub">
              {m.minutes} min · needs {m.crew} crew
            </div>
          </div>
        </div>
        <p class="hv-d-desc">{m.description}</p>
        <div class="section-h">Hazards</div>
        <div class="hv-hz-row">
          {m.hazards.map((hz) => (
            <HazardChip key={hz} hz={hz} ok={shown.countered.includes(hz)} />
          ))}
        </div>
        <div class="section-h">Rewards</div>
        <div class="hv-rewards">
          {R.items.map((it) => (
            <ItemIcon key={it.id} id={it.id} size={34} count={it.min === it.max ? it.max : `${it.min}–${it.max}`} />
          ))}
          <span class="hv-rw-extra">
            <i class="coin silver" />
            {`${R.silver[0].toLocaleString('en-US')}–${R.silver[1].toLocaleString('en-US')}`}
          </span>
          {R.gold && <span class="hv-rw-extra gold">{`${R.gold[0]}–${R.gold[1]} gold`}</span>}
          {R.card && <span class="hv-rw-extra card">{`${Math.round(R.card.chance * 100)}% card`}</span>}
        </div>
        {!run && unlocked && (
          <>
            <div class="section-h">
              Assign crew{' '}
              <button class="hv-link" onClick={() => setCrew(autoCrew(h, m))}>
                Auto-assign
              </button>
            </div>
            <div class="hv-crew">
              {h.crew.map((n) => {
                const def = CREW[n];
                const away = busy.has(n);
                const on = picked.includes(n);
                const helps = def && m.hazards.includes(def.counters);
                return (
                  <button key={n} class={`hv-crew-chip${on ? ' on' : ''}${helps ? ' helps' : ''}`} disabled={away} onClick={() => toggle(n)}>
                    <b>{n}</b>
                    <span>{def ? `${def.trait} · ${HAZARDS[def.counters].name}` : 'Deckhand'}</span>
                    {away && <em>Away</em>}
                  </button>
                );
              })}
            </div>
          </>
        )}
        <div class="hv-odds">
          <div>
            <span class="muted">Success</span> <b>{Math.round(shown.success * 100)}%</b>
          </div>
          <Bar tone="teal" value={shown.success} />
          <div>
            <span class="muted">Great Success</span> <b class="great">{Math.round(shown.great * 100)}%</b>
          </div>
        </div>
        <div class="hv-d-foot">
          {run ? (
            <div class="hv-progress">
              <Bar tone="teal" value={1 - (run.doneAt - x.now) / (m.minutes * MIN)} label={`${run.crew.join(', ')} return in ${formatDuration(run.doneAt - x.now)}`} />
            </div>
          ) : !unlocked ? (
            <div class="hv-note">Requires Haven Lv {m.havenLevel}</div>
          ) : (
            <button class="btn btn--gold hv-go" disabled={picked.length < m.crew} onClick={() => x.act(startDispatch(x.a, m.id, picked, Date.now()), `${picked.join(', ')} set sail: ${m.name}`)}>
              {picked.length < m.crew ? `Assign ${m.crew - picked.length} more crew` : `Dispatch (${m.minutes} min)`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ================================================================ garden

function Plot({ crop, stage }: { crop: CropDef | null; stage: number }) {
  const hue = cssHue(crop?.hue ?? 0x7bd66b);
  const plants: JSX.Element[] = [];
  if (crop) {
    for (let row = 0; row < 3; row++) {
      for (let i = 0; i < 12; i++) {
        const x = 50 + i * 56 + (row % 2) * 24;
        const y = 50 + row * 36;
        const k = `${row}-${i}`;
        if (stage === 0) plants.push(<ellipse key={k} cx={x} cy={y - 2} rx="5" ry="2.5" fill="#6a4a2a" />);
        else {
          const hgt = stage === 1 ? 8 : stage === 2 ? 14 : 18;
          plants.push(
            <g key={k} class={stage >= 3 ? 'hv-bloom' : undefined}>
              <path d={`M${x} ${y} L${x} ${y - hgt}`} stroke="#4a8a3a" stroke-width="2" />
              <path d={`M${x} ${y - hgt * 0.45} q-7 -4 -9 -10 q7 1 9 8 Z M${x} ${y - hgt * 0.6} q7 -4 9 -10 q-7 1 -9 8 Z`} fill="#5aa84a" />
              {stage >= 2 && <circle cx={x} cy={y - hgt} r={stage >= 3 ? 4.5 : 2.6} fill={stage >= 3 ? hue : '#9ac070'} />}
            </g>,
          );
        }
      }
    }
  }
  return (
    <svg class="hv-plot-svg" viewBox="0 0 740 150" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id="hvSoil" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#3a2a1a" />
          <stop offset="1" stop-color="#1e150d" />
        </linearGradient>
      </defs>
      <rect width="740" height="150" fill="#16200f" />
      {[0, 1, 2].map((row) => (
        <rect key={row} x="20" y={40 + row * 36} width="700" height="18" rx="6" fill="url(#hvSoil)" stroke="#0e0a06" />
      ))}
      {plants}
    </svg>
  );
}

function GardenTab({ x }: { x: Ctx }) {
  const h = x.a.haven;
  const v = gardenView(h, x.now);
  const crop = v ? (CROPS.find((c) => c.id === v.crop) ?? null) : null;
  const mult = 1 + havenBonus(x.a, 'gardenYield');
  const lvl = havenLevelProgress(h.xp).level;
  const harvest = () => {
    const r = harvestGarden(x.a, Date.now());
    const prog = x.game.progression;
    if (!r || !prog) return;
    for (const it of r.items) prog.giveItem(it.id, it.count);
    if (r.level) x.game.hud.announce(`Haven Isle reached level ${r.level}`, 'success');
    x.act({ ok: true });
  };
  return (
    <div class="hv-garden">
      <div class={`hv-plot${v?.stage === 3 ? ' ripe' : ''}`}>
        <Plot crop={crop} stage={v?.stage ?? 0} />
        <div class="hv-plot-info">
          {v && crop ? (
            <>
              <div class="hv-d-name">{crop.name}</div>
              <div class="hv-stage">{GROWTH_STAGES[v.stage]}</div>
              {v.stage < 3 ? (
                <Bar tone="green" value={v.pct} label={`Ready in ${formatDuration(v.readyAt - x.now)}`} />
              ) : (
                <button class="btn btn--gold hv-go" onClick={harvest}>
                  Harvest
                </button>
              )}
            </>
          ) : (
            <>
              <div class="hv-d-name">The herb bed lies fallow</div>
              <div class="hv-stage">Choose seeds below to plant.</div>
            </>
          )}
        </div>
      </div>
      <div class="section-h">Seeds {mult > 1 && <small>Fertile Terraces: harvest ×{mult.toFixed(2)}</small>}</div>
      <div class="hv-crops">
        {CROPS.map((c) => {
          const locked = lvl < c.havenLevel;
          return (
            <div key={c.id} class={`hv-crop${locked ? ' locked' : ''}`} style={{ '--crop': cssHue(c.hue) }}>
              <div class="hv-crop-name">{c.name}</div>
              <div class="hv-crop-yield">
                {c.yields.map((y) => (
                  <ItemIcon key={y.id} id={y.id} size={30} count={Math.max(1, Math.round(y.count * mult))} />
                ))}
              </div>
              <div class="hv-crop-meta">
                <span class="hv-time">
                  <Clock /> {c.minutes} min
                </span>
                <Silver n={c.seeds} />
              </div>
              <button class="btn" disabled={locked || !!h.garden || x.a.wallet.silver < c.seeds} onClick={() => x.act(plantCrop(x.a, c.id, Date.now()), `Planted ${c.name}`)}>
                {locked ? `Haven Lv ${c.havenLevel}` : h.garden ? 'Bed in use' : 'Plant'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ================================================================ window

export function HavenPanel() {
  void ui.version.value;
  const arg = ui.panelArg.value;
  const asTab = (s: string | null): HavenTab | null => (s && (HAVEN_TABS as string[]).includes(s) ? (s as HavenTab) : null);
  const [tab, setTab] = useState<HavenTab>(asTab(arg) ?? 'research');
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const t = asTab(arg);
    if (t) setTab(t);
  }, [arg]);
  const game = havenGame.current;
  const c = game?.progression?.char;
  if (!game || !c)
    return (
      <Window title="Haven Isle" width={420}>
        <p class="muted">No character loaded.</p>
      </Window>
    );
  const a = game.account;
  const h = a.haven;
  const x: Ctx = {
    game,
    a,
    c,
    now,
    act: (r, ok) => {
      if (!r.ok) game.hud.toast(r.reason, '#e5484d');
      else {
        if (ok) game.hud.toast(ok, '#7ad8ff');
        game.store.saveSoon(a);
      }
      bump();
    },
  };
  const prog = havenLevelProgress(h.xp);
  const next = nextHavenTimer(h);
  const busy = busyCrew(h).size;
  const gv = gardenView(h, now);
  const badge: Record<HavenTab, string | null> = {
    research: h.researching ? formatDuration(h.researching.doneAt - now) : null,
    workshop: h.crafting.length ? `${h.crafting.length}` : null,
    dispatch: h.dispatches.length ? `${h.dispatches.length}` : null,
    garden: gv ? (gv.stage >= 3 ? 'Ready' : `${Math.floor(gv.pct * 100)}%`) : null,
  };
  return (
    <Window title="Haven Isle" width={940} className="hv-window">
      <div class="hv-header">
        <div class="hv-crest">
          <svg viewBox="0 0 48 48">
            <path d="M24 3 L43 13 L43 35 L24 45 L5 35 L5 13 Z" class="hv-crest-frame" />
            <path d="M12 31 Q18 22 24 26 Q30 18 36 31 Z" class="hv-crest-isle" />
            <path d="M10 34 Q17 31 24 34 Q31 37 38 34" class="hv-crest-wave" />
          </svg>
          <b>{prog.level}</b>
        </div>
        <div class="hv-title">
          <div class="hv-name">
            Stronghold <span>Level {prog.level}</span>
          </div>
          <Bar tone="gold" value={prog.need ? prog.into / prog.need : 1} label={prog.need ? `${prog.into} / ${prog.need} XP` : 'Max level'} />
        </div>
        <div class="hv-stats">
          <div>
            <span>Silver</span>
            <Silver n={a.wallet.silver} />
          </div>
          <div>
            <span>Crew at harbour</span>
            <b>
              {h.crew.length - busy} / {h.crew.length}
            </b>
          </div>
          <div>
            <span>Next completion</span>
            <b>{next ? formatDuration(next - now) : '—'}</b>
          </div>
        </div>
      </div>
      <div class="hv-tabs" role="tablist">
        {HAVEN_TABS.map((t) => (
          <button key={t} class="tab" role="tab" aria-selected={t === tab} onClick={() => setTab(t)}>
            {TAB_LABEL[t]}
            {badge[t] && <span class={`hv-badge${t === 'garden' && badge[t] === 'Ready' ? ' ready' : ''}`}>{badge[t]}</span>}
          </button>
        ))}
      </div>
      <div class="hv-body">
        {tab === 'research' && <ResearchTab x={x} />}
        {tab === 'workshop' && <WorkshopTab x={x} />}
        {tab === 'dispatch' && <DispatchTab x={x} />}
        {tab === 'garden' && <GardenTab x={x} />}
      </div>
    </Window>
  );
}
